import React, { useState, useEffect } from 'react';
import '../styles/AutomationCard.css';
import { Badge, Button, Modal, Input } from './'; // SelectionChip, Dropdown, ExitScreen no longer needed here
import { Arrow } from '../assets/icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { AiMail02Icon, Message01Icon, Clock05Icon, TokenCircleIcon, PauseIcon, Delete02Icon, PlayIcon } from '@hugeicons/core-free-icons';
import { colour } from '../styles/GlobalStyles';
import { setAutomationTransaction, deleteAutomation, setAutomation } from '../functions';
import { useUI } from '../contexts';
import IngestionSection from './AutomationCard/IngestionSection';
import UnderwritingSection from './AutomationCard/UnderwritingSection';
import ProfitRulesSection from './AutomationCard/ProfitRulesSection';
import OutputSection from './AutomationCard/OutputSection';
import { getPrimaryIngestionType, getAutomationCounters, getIngestionLogo } from './AutomationCard/utils';

function AutomationCard(props) {
    const automation = props.automation;
    const companyId = props.companyId;
    const onUpdate = props.onUpdate;
    const onDelete = props.onDelete;
    const onEnterDetailView = props.onEnterDetailView;
    const onExitDetailView = props.onExitDetailView;
    const { setShowDarkBackground } = useUI();
    
    const [isDetailedView, setIsDetailedView] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [pausing, setPausing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    // Form state for Name only
    const [formName, setFormName] = useState('');
    const [nameSaving, setNameSaving] = useState(false);
    const [nameHasChanges, setNameHasChanges] = useState(false);
    
    // Initialize form state when automation changes or detailed view opens
    useEffect(() => {
        if (isDetailedView && automation) {
            setFormName(automation.name || '');
            setNameHasChanges(false);
        }
    }, [isDetailedView, automation]);
    
    // Check for changes
    useEffect(() => {
        if (isDetailedView && automation) {
            const originalName = automation.name || '';
            setNameHasChanges(formName !== originalName);
        }
    }, [formName, isDetailedView, automation]);
    
    // Control dark background when delete modal is open
    useEffect(() => {
        setShowDarkBackground(deleteModalOpen);
        return () => {
            setShowDarkBackground(false);
        };
    }, [deleteModalOpen, setShowDarkBackground]);
    
    if (!automation) {
        return null;
    }

    const isActive = automation.active === true;
    const badgeText = isActive ? "Active" : "Inactive";
    const badgeBgColour = isActive ? "bg-colour-green-01" : "bg-colour-light";
    
    // Get logo based on ingestion type
    const ingestionType = getPrimaryIngestionType(automation);
    const isMLS = ingestionType === 'mls_buy_box'; // Determine if MLS based on type
    const ingestionLogo = getIngestionLogo(isMLS, ingestionType);
    
    const showEmailIcon = automation.output?.notifications?.recipients?.some(r => r.emailEnabled === true);
    const showSmsIcon = automation.output?.notifications?.recipients?.some(r => r.smsEnabled === true);
    
    const { reports, goldenLeads } = getAutomationCounters(automation);
    
    const handleCardClick = () => {
        setIsDetailedView(true);
        if (onEnterDetailView) {
            onEnterDetailView();
        }
    };

    const handleBackClick = () => {
        setIsDetailedView(false);
        if (onExitDetailView) {
            onExitDetailView();
        }
    };

    const handlePause = async () => {
        if (!companyId) return;
        setPausing(true);
        try {
            const newActiveState = !isActive;
            const result = await setAutomationTransaction(companyId, automation.id, "active", newActiveState);
            if (result.status === 200) {
                if (onUpdate) {
                    onUpdate({ ...automation, active: newActiveState });
                }
            } else {
                alert("Failed to update automation. Please try again.");
            }
        } catch (error) {
            console.error("Error pausing automation:", error);
            alert("Failed to update automation. Please try again.");
        } finally {
            setPausing(false);
        }
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
    };

    const confirmDelete = async () => {
        if (!companyId) return;
        setDeleting(true);
        try {
            const result = await deleteAutomation(companyId, automation.id);
            if (result.status === 200) {
                if (onDelete) {
                    onDelete();
                }
            } else {
                alert("Failed to delete automation. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting automation:", error);
            alert("Failed to delete automation. Please try again.");
        } finally {
            setDeleting(false);
            closeDeleteModal();
        }
    };
    
    const handleSaveName = async () => {
        if (!companyId || !nameHasChanges) return;
        setNameSaving(true);
        try {
            const payload = { name: formName };
            const result = await setAutomation(companyId, automation.id, payload); // Assuming automation.id is available, wait props.automationId
            
            // automationId is passed in props? No, it's automation.id
            // Check props again. key={automation.id} automation={automation} automationId={automation.id}
            
            if (result.status === 200) {
                if (onUpdate) {
                    onUpdate({ ...automation, name: formName });
                }
                setNameHasChanges(false);
            } else {
                alert("Failed to update name. Please try again.");
            }
        } catch (error) {
            console.error("Error updating name:", error);
            alert("Failed to update name. Please try again.");
        } finally {
            setNameSaving(false);
        }
    };

    // Detailed view
    if (isDetailedView) {
        return (
            <>
                {deleteModalOpen && (
                    <Modal
                        heading="Delete Automation"
                        subheading={null}
                        body={
                            <span className="body-regular colour-primary">
                                Are you sure you want to delete this automation? This action cannot be undone.
                            </span>
                        }
                        type="destructive"
                        cancelText="Cancel"
                        cancelButtonCTA={closeDeleteModal}
                        actionButtonText="Delete"
                        actionButtonCTA={confirmDelete}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeDeleteModal}
                        loading={deleting}
                        actionButtonDisabled={deleting}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass="delete-automation-modal"
                    />
                )}
                <div className="automation-detail-container">
                    <div className="automation-detail-header">
                        <div className="automation-detail-header-left">
                            <div className="automation-detail-header-back-button">
                                <Button
                                    buttonType="light-button"
                                    size="large"
                                    text="Back"
                                    cta={handleBackClick}
                                    disabled={false}
                                    leftIcon={<Arrow fill={colour.gray06} className="" />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                            <div className="automation-detail-info-section">
                                <div className="automation-detail-title-row">
                                    <h2 className="heading-small-regular colour-primary">
                                        {automation.name}
                                    </h2>
                                    <Badge
                                        bgColour={badgeBgColour}
                                        statusDot={false}
                                        statusDotColour={null}
                                        text={badgeText}
                                        textClass="body-regular colour-primary"
                                        icon={null}
                                        tooltip={false}
                                        tooltipBody={null}
                                        tooltipPosition={null}
                                        iconLeft={false}
                                    />
                                </div>
                                <div className="automation-detail-stats-row">
                                    <div className="automation-detail-stat-row">
                                        <HugeiconsIcon 
                                            icon={Clock05Icon} 
                                            size={16} 
                                            color={colour.gray04} 
                                        />
                                        <span className="body-regular colour-secondary">
                                            Generated {reports} reports
                                        </span>
                                    </div>
                                    <div className="automation-detail-stat-row">
                                        <HugeiconsIcon 
                                            icon={TokenCircleIcon} 
                                            size={16} 
                                            color={colour.gray04} 
                                        />
                                        <span className="body-regular colour-secondary">
                                            Detected {goldenLeads} golden leads
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="automation-detail-header-controls">
                            <Button
                                buttonType="destructive-button"
                                size="large"
                                text="Delete"
                                cta={handleDeleteClick}
                                disabled={deleting || pausing}
                                leftIcon={<HugeiconsIcon icon={Delete02Icon} color={colour.red03} size={16} />}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={deleting}
                            />
                            <Button
                                buttonType="secondary-button"
                                size="large"
                                text={isActive ? "Pause" : "Resume"}
                                cta={handlePause}
                                disabled={deleting || pausing}
                                leftIcon={<HugeiconsIcon icon={isActive ? PauseIcon : PlayIcon} color={colour.gray06} size={16} />}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={pausing}
                            />
                        </div>
                    </div>
                    <div className="automation-detail-body">
                        <div className="automation-form-section">
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <Input
                                        label="Automation name"
                                        labelType="default"
                                        info={false}
                                        value={formName}
                                        onChange={(e) => setFormName(e.target.value)}
                                        placeholder="EXP Realty acquisitions - X ingestion"
                                        type="text"
                                        leftUnit={null}
                                        rightUnit={null}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={false}
                                        multiline={false}
                                        error={false}
                                        onMouseEnter={null}
                                        onMouseLeave={null}
                                        reset={false}
                                        resetFunc={null}
                                        resetPosition={null}
                                        focusMultiline={false}
                                        prompt={false}
                                        submitPromptDisabled={false}
                                        submitPrompt={null}
                                        onHeightChange={null}
                                    />
                                </div>
                                {nameHasChanges && (
                                    <div style={{ paddingBottom: '2px' }}>
                                        <Button
                                            buttonType="primary-button"
                                            size="medium"
                                            text="Save"
                                            cta={handleSaveName}
                                            disabled={nameSaving}
                                            leftIcon={null}
                                            leftIconClass=""
                                            rightIcon={null}
                                            rightIconClass=""
                                            alt=""
                                            standardIcon={false}
                                            badge={false}
                                            badgeVal={null}
                                            badgeBG={null}
                                            buttonRef={null}
                                            spinner={nameSaving}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="automation-form-divider"></div>
                        
                        <IngestionSection 
                            automation={automation}
                            isMLS={isMLS}
                            onUpdate={onUpdate}
                            companyId={companyId}
                            automationId={props.automationId}
                        />
                        
                        <UnderwritingSection 
                            automation={automation}
                            onUpdate={onUpdate}
                            companyId={companyId}
                            automationId={props.automationId}
                        />
                        
                        <ProfitRulesSection 
                            automation={automation}
                            onUpdate={onUpdate}
                            companyId={companyId}
                        />
                        
                        <OutputSection 
                            automation={automation}
                            isMLS={isMLS}
                            onUpdate={onUpdate}
                            companyId={companyId}
                            automationId={props.automationId}
                        />
                    </div>
                </div>
            </>
        );
    }

    // Card view
    return (
        <div className="automation-card-container" onClick={handleCardClick}>
            <div className="automation-card-header">
                <Badge
                    bgColour={badgeBgColour}
                    statusDot={false}
                    statusDotColour={null}
                    text={badgeText}
                    textClass="body-regular colour-primary"
                    icon={null}
                    tooltip={false}
                    tooltipBody={null}
                    tooltipPosition={null}
                    iconLeft={false}
                />
                <div className="automation-card-sources">
                    {ingestionLogo}
                    {showEmailIcon && (
                        <HugeiconsIcon 
                            icon={AiMail02Icon} 
                            size={16} 
                            color={colour.gray06} 
                        />
                    )}
                    {showSmsIcon && (
                        <HugeiconsIcon 
                            icon={Message01Icon} 
                            size={16} 
                            color={colour.gray06} 
                        />
                    )}
                </div>
            </div>
            <h3 className="automation-card-title body-bold colour-primary">
                {automation.name}
            </h3>
            <div className="automation-card-stats">
                <div className="automation-card-stat-row">
                    <HugeiconsIcon 
                        icon={Clock05Icon} 
                        size={16} 
                        color={colour.gray04} 
                    />
                    <span className="body-regular colour-secondary">
                        Generated {reports} reports
                    </span>
                </div>
                <div className="automation-card-stat-row">
                    <HugeiconsIcon 
                        icon={TokenCircleIcon} 
                        size={16} 
                        color={colour.gray04} 
                    />
                    <span className="body-regular colour-secondary">
                        Detected {goldenLeads} golden leads
                    </span>
                </div>
            </div>
        </div>
    );
}

export default AutomationCard;
